import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { PersonPicker } from "@vixio/core-ui";
import { from, rpc, unwrap, useAction, useHasRole, useOrg, type PersonLite } from "@vixio/data";
import { Button, DataTable, Dialog, KpiRow, KpiTile, NumberInput, PageHeader, Panel, Select, StatusFromMap, TextArea, TextInput, formatDate, formatInt } from "@vixio/ui";
import { CATEGORY, IMPACT, LEVEL, PROBABILITY, levelOf, type MatrixCell, type RiskView } from "./api";
import { RiskMatrix, type Cell } from "./Matrix";

export function useRisks(orgId: string) {
  return useQuery({ queryKey: ["risks", "list", orgId], queryFn: () => unwrap<RiskView[]>(from("risks", "v_risks").select("*").eq("org_id", orgId).order("score", { ascending: false }).order("code")) });
}

export function useMatrix(orgId: string) {
  return useQuery({ queryKey: ["risks", "matrix", orgId], queryFn: () => unwrap<MatrixCell[]>(from("risks", "v_matrix").select("*").eq("org_id", orgId)) });
}

export function RisksPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const canWrite = useHasRole("manager");
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const risks = useRisks(org.id);
  const matrix = useMatrix(org.id);
  const p = Number(params.get("p")) || 0;
  const i = Number(params.get("i")) || 0;
  const cell: Cell = p && i ? { probability: p, impact: i } : null;
  const all = risks.data ?? [];
  const active = all.filter((r) => r.status === "ativo");
  const rows = cell ? active.filter((r) => r.probability === cell.probability && r.impact === cell.impact) : all;
  const overdue = active.filter((r) => r.review_overdue);
  const up = active.filter((r) => (r.last_delta ?? 0) > 0);
  return (
    <div className="vx-content">
      <PageHeader
        title="Riscos"
        description="Matriz de probabilidade e impacto, sinais de alerta, mitigação e revisão periódica com histórico das avaliações."
        actions={canWrite ? <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>Novo risco</Button> : null}
      />
      <KpiRow>
        <KpiTile label="Riscos ativos" value={formatInt(active.length)} foot={`${all.length - active.length} encerrado(s)`} />
        <KpiTile label="Críticos" value={formatInt(active.filter((r) => r.level === "critico").length)} foot={`${active.filter((r) => r.level === "alto").length} alto(s)`} tone={active.some((r) => r.level === "critico") ? "crit" : undefined} />
        <KpiTile label="Revisões atrasadas" value={formatInt(overdue.length)} foot={overdue.length ? overdue.slice(0, 4).map((r) => r.code).join(", ") : "em dia"} tone={overdue.length ? "warn" : undefined} />
        <KpiTile label="Subiram na última avaliação" value={formatInt(up.length)} foot={up.length ? up.slice(0, 4).map((r) => r.code).join(", ") : undefined} tone={up.length ? "warn" : undefined} />
      </KpiRow>
      <div className="vx-section vx-grid vx-grid--2">
        <Panel title="Matriz 5 × 5" actions={<span className="text-2" style={{ fontSize: "var(--fs-12)" }}>riscos ativos; clique para filtrar</span>}>
          <RiskMatrix cells={matrix.data ?? []} selected={cell} onSelect={(c) => setParams(c ? { p: String(c.probability), i: String(c.impact) } : {}, { replace: true })} />
        </Panel>
        <Panel title="Próximas revisões">
          <ul className="vx-stack" style={{ gap: 10, margin: 0, padding: 0, listStyle: "none" }}>
            {[...active].sort((x, y) => x.next_review_on.localeCompare(y.next_review_on)).slice(0, 7).map((r) => (
              <li key={r.id} className="vx-row vx-row--between" style={{ flexWrap: "nowrap", gap: 12 }}>
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Link to={`/gestao/riscos/${r.id}`}><span className="mono">{r.code}</span> {r.title}</Link>
                </span>
                <span className={r.review_overdue ? "num text-crit" : "num text-2"} style={{ whiteSpace: "nowrap" }}>{r.review_overdue ? "atrasada" : formatDate(r.next_review_on)}</span>
              </li>
            ))}
          </ul>
          {active.length === 0 ? <p className="muted" style={{ margin: 0 }}>Nenhum risco ativo.</p> : null}
        </Panel>
      </div>
      <div className="vx-section">
        <DataTable<RiskView>
          caption={cell ? `Riscos com probabilidade ${cell.probability} e impacto ${cell.impact}` : "Riscos"}
          data={rows}
          loading={risks.isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/gestao/riscos/${r.id}`)}
          searchKeys={["code", "title", "warning_signal"]}
          searchPlaceholder="Buscar risco"
          exportName="riscos"
          filters={[
            { key: "category", label: "Categoria", options: Object.entries(CATEGORY).map(([value, label]) => ({ value, label })) },
            { key: "level", label: "Nível", options: Object.entries(LEVEL).map(([value, s]) => ({ value, label: s.label })) },
            { key: "status", label: "Situação", options: [{ value: "ativo", label: "Ativos" }, { value: "encerrado", label: "Encerrados" }] },
          ]}
          initialFilters={{ status: "ativo" }}
          toolbar={cell ? <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>Limpar célula {cell.probability}×{cell.impact}</Button> : null}
          empty="Nenhum risco."
          columns={[
            { key: "code", header: "Risco", cell: (r) => <span className="vx-table__primary" style={{ display: "block", minWidth: 190 }}><Link to={`/gestao/riscos/${r.id}`} onClick={(e) => e.stopPropagation()}><span className="mono">{r.code}</span> {r.title}</Link><span className="vx-table__sub">{CATEGORY[r.category]}{r.owner_name ? ` · ${r.owner_name}` : ""}</span></span>, csv: (r) => `${r.code} ${r.title}` },
            { key: "score", header: "Nota", align: "right", sortable: true, cell: (r) => <span className="vx-table__primary"><span className="num">{r.probability}×{r.impact} = <strong>{r.score}</strong></span>{r.last_delta ? <span className={r.last_delta > 0 ? "vx-table__sub text-crit num" : "vx-table__sub text-ok num"}>{r.last_delta > 0 ? `subiu ${r.last_delta}` : `caiu ${-r.last_delta}`}</span> : null}</span>, csv: (r) => r.score },
            { key: "level", header: "Nível", cell: (r) => <StatusFromMap map={LEVEL} value={r.level} />, csv: (r) => LEVEL[r.level]?.label ?? r.level },
            { key: "next_review_on", header: "Revisão", sortable: true, cell: (r) => (r.status !== "ativo" ? <span className="muted">encerrado</span> : <span className="vx-table__primary"><span className={r.review_overdue ? "num text-crit" : "num"}>{formatDate(r.next_review_on)}</span><span className="vx-table__sub">{r.review_overdue ? "atrasada" : `a cada ${r.review_every_days} dias`}</span></span>), csv: (r) => r.next_review_on },
          ]}
        />
      </div>
      <RiskDialog risk={null} open={creating} onOpenChange={setCreating} />
    </div>
  );
}

export function RiskDialog({ risk, open, onOpenChange }: { risk: RiskView | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const navigate = useNavigate();
  const [person, setPerson] = useState<PersonLite | null>(null);
  const [f, setF] = useState({ code: "", title: "", category: "operacao", description: "", signal: "", mitigation: "", p: "3", i: "3", label: "", every: 30 as number | null, source: "" });
  const nextCode = useQuery({
    queryKey: ["risks", "next_code", org.id],
    enabled: open && !risk,
    queryFn: async () => {
      const rows = await unwrap<Array<{ code: string }>>(from("risks", "risks").select("code").eq("org_id", org.id));
      const max = rows.reduce((m, r) => Math.max(m, Number(r.code.slice(1)) || 0), 0);
      return `R${String(max + 1).padStart(2, "0")}`;
    },
  });
  useEffect(() => {
    if (!open) return;
    setPerson(risk?.owner_person_id ? ({ id: risk.owner_person_id, full_name: risk.owner_name ?? "" } as PersonLite) : null);
    setF({
      code: risk?.code ?? "", title: risk?.title ?? "", category: risk?.category ?? "operacao", description: risk?.description ?? "", signal: risk?.warning_signal ?? "", mitigation: risk?.mitigation ?? "",
      p: String(risk?.probability ?? 3), i: String(risk?.impact ?? 3), label: risk && !risk.owner_person_id ? risk.owner_name ?? "" : "", every: risk?.review_every_days ?? 30, source: risk?.source_ref ?? "",
    });
  }, [open, risk]);
  useEffect(() => {
    if (open && !risk && nextCode.data) setF((x) => (x.code ? x : { ...x, code: nextCode.data }));
  }, [open, risk, nextCode.data]);
  const ok = /^R[0-9]{2,3}$/.test(f.code) && f.title.trim().length >= 3 && f.description.trim().length >= 3 && f.signal.trim().length >= 3 && f.mitigation.trim().length >= 3 && f.every !== null && f.every >= 7 && f.every <= 365;
  const save = useAction(
    () =>
      rpc("risks", "upsert_risk", {
        p_org: org.id, p_code: f.code, p_title: f.title.trim(), p_category: f.category, p_description: f.description.trim(), p_warning_signal: f.signal.trim(), p_mitigation: f.mitigation.trim(),
        p_probability: Number(f.p), p_impact: Number(f.i), p_owner_person: person?.id ?? null, p_owner_label: person ? null : f.label.trim() || null, p_review_every_days: f.every ?? 30, p_source_ref: f.source.trim() || null,
      }),
    { invalidate: [["risks"]], success: risk ? "Risco atualizado" : "Risco cadastrado com a avaliação inicial", onSuccess: (id) => { onOpenChange(false); if (!risk) navigate(`/gestao/riscos/${id}`); } },
  );
  const score = Number(f.p) * Number(f.i);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" title={risk ? `Editar ${risk.code}` : "Novo risco"} description={risk ? "Probabilidade e impacto mudam só por uma avaliação, que fica no histórico." : undefined} onSubmit={() => ok && save.mutate(undefined)} footer={<><Button onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" variant="primary" loading={save.isPending} disabled={!ok}>Salvar</Button></>}>
      <div className="vx-form-grid">
        <TextInput label="Código" value={f.code} onChange={(v) => setF({ ...f, code: v.toUpperCase() })} disabled={Boolean(risk)} className="mono" required hint="R01, R02..." />
        <Select label="Categoria" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={Object.entries(CATEGORY).map(([value, label]) => ({ value, label }))} />
        <TextInput className="span-2" label="Título" value={f.title} onChange={(v) => setF({ ...f, title: v })} required />
        <TextArea className="span-2" label="Descrição" value={f.description} onChange={(v) => setF({ ...f, description: v })} rows={3} required />
        <TextArea className="span-2" label="Sinal de alerta" value={f.signal} onChange={(v) => setF({ ...f, signal: v })} rows={2} required hint="O que observar para saber que o risco está se materializando." />
        <TextArea className="span-2" label="Mitigação" value={f.mitigation} onChange={(v) => setF({ ...f, mitigation: v })} rows={3} required />
        {!risk ? (
          <>
            <Select label="Probabilidade" value={f.p} onChange={(v) => setF({ ...f, p: v })} options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} · ${PROBABILITY[n]}` }))} />
            <Select label="Impacto" value={f.i} onChange={(v) => setF({ ...f, i: v })} options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} · ${IMPACT[n]}` }))} hint={`Nota ${score}, nível ${LEVEL[levelOf(score)]!.label}.`} />
          </>
        ) : null}
        <div className="span-2"><PersonPicker label="Dono do risco" value={person} onChange={setPerson} hint="Recebe o aviso de revisão." /></div>
        {!person ? <TextInput label="Ou descreva o dono" value={f.label} onChange={(v) => setF({ ...f, label: v })} hint="Ex.: Fundador, Financeiro." /> : null}
        <NumberInput label="Revisar a cada" value={f.every} onChange={(v) => setF({ ...f, every: v })} min={7} max={365} suffix="dias" />
        <TextInput label="Referência" value={f.source} onChange={(v) => setF({ ...f, source: v })} hint="Ex.: seção do plano mestre." />
      </div>
    </Dialog>
  );
}
